import React, { useState, useEffect, useRef } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { userLoggedOut } from "../../actions/dashboardActions";
import PageLayout from "../../components/PageLayout";
import ScrollToTop from "../../components/utils/ScrollToTop";
import Card from "../../components/Card";
import Button from "../../components/Button";
import { resetSession } from "../../shared/session";

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function LoginCode() {
  const { loginId } = useParams();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const [state, setState] = useState({
    loading: true,
    error: null,
    redirect: false,
    manualVerification: false,
    email: null,
    verifying: false,
  });

  // Prevents duplicate login token consumption caused by React 18 StrictMode
  // double-mounting effects in development. The POST consumes the token on
  // first call; a second POST would get a 403 because the token is already
  // used. The ref persists across the StrictMode unmount-remount cycle, so
  // the second mount sees hasInitiated=true and skips the fetch.
  const hasInitiated = useRef(false);

  const postLogin = async (isManualVerification = false) => {
    if (!UUID_PATTERN.test(loginId)) {
      setState((prevState) => ({ ...prevState, loading: false, error: "This link is invalid" }));
      return;
    }
    try {
      const response = await fetch("/api/login/token/" + loginId, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
        },
        body: isManualVerification ? JSON.stringify({ manual_verification: true }) : undefined,
      });
      const data = await response.json();
      if (data.status === "success") {
        resetSession(queryClient);
        dispatch(userLoggedOut());
        setState((prevState) => ({
          ...prevState,
          loading: false,
          redirect: true,
          manualVerification: false,
        }));
      } else if (data.status === "manual_verification_needed") {
        setState((prevState) => ({
          ...prevState,
          loading: false,
          manualVerification: true,
          email: data.email,
        }));
      } else if (data.status === "invalid_token") {
        setState((prevState) => ({
          ...prevState,
          loading: false,
          error: "This link is invalid",
        }));
      } else if (data.status === "already_used") {
        setState((prevState) => ({
          ...prevState,
          loading: false,
          error: "It looks like you've already used this link",
        }));
      } else if (data.status === "expired") {
        setState((prevState) => ({
          ...prevState,
          loading: false,
          error: "This link has expired",
        }));
      } else if (data.status === "failed") {
        setState((prevState) => ({
          ...prevState,
          loading: false,
          error: "Failed to load login details.",
        }));
      }
    } catch (error) {
      console.error("Failed to load login details", error);
      setState((prevState) => ({
        ...prevState,
        loading: false,
        error: "Failed to load login details.",
      }));
    }
  };

  const handleManualVerification = () => {
    setState((prevState) => ({ ...prevState, verifying: true }));
    postLogin(true).finally(() => {
      setState((prevState) => ({ ...prevState, verifying: false }));
    });
  };

  useEffect(() => {
    if (hasInitiated.current) return;
    hasInitiated.current = true;
    postLogin(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- fires once on mount; ref guard prevents StrictMode re-fire; loginId is stable from URL params

  if (state.loading) {
    return null;
  }

  if (state.error) {
    return (
      <PageLayout>
        <ScrollToTop />
        <Card title="Sorry, that didn't work" removeTopPadding headingSize="1">
          <p>{state.error}. You'll need a new link.</p>

          <p>
            Try <Link to="/login">signing in</Link> again to get one.
          </p>
        </Card>
      </PageLayout>
    );
  }

  if (state.manualVerification) {
    return (
      <PageLayout>
        <ScrollToTop />
        <Card title={`Confirm you want to log in as ${state.email}`} removeTopPadding headingSize="1">
          <p>This helps keep your dashboard secure.</p>
          <Button onClick={handleManualVerification} disabled={state.verifying}>
            Yes, log me in
          </Button>
        </Card>
      </PageLayout>
    );
  }

  if (state.redirect) {
    return <Navigate to="/dashboard" />;
  }
}

export default LoginCode;
